import { ChatAltIcon } from "@heroicons/react/outline";
import { isEmpty, keys, map, mean } from "lodash";
import React, { useCallback, useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import HexIcon from "../../../components/icons/HexIcon";
import FilterLabels from "../../../components/labels/FilterLabels";
import VirtualScroll from "../../../components/VirtualScroll";
import { useDiscordChannels } from "../../../context/discordChannels";
import { useFilteredList } from "../../../context/filteredList";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useLabels } from "../../../context/labels";
import { useLoading } from "../../../context/loading";
import { LoadingKey } from "../../../context/loading/LoadingProvider";
import { useViewport } from "../../../context/viewport";
import classNames from "../../../utils/classNames";
import { breakpoints, Entity } from "../../../utils/constants";
import {
  classificationColorMap,
  ClassificationValue,
  getClassification
} from "../../../utils/hotspotUtils";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../utils/useFilteredLabels";
import useSideMenu from "./useSideMenu";

interface Props {}

const HexesList: React.FC<Props> = () => {
  const browserLocation = useLocation();
  const history = useHistory();
  const { getIsReady } = useLoading();
  const toggleSideMenu = useSideMenu((state) => state.toggleSideMenu);
  const { channels } = useDiscordChannels();
  const { filteredList, smartLabelFilters } = useFilteredList();
  const { labels } = useLabels();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);

  const filteredHexLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapHexes],
        labels,
        smartLabelFilters[Entity.hex],
        Entity.hex
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredHexes = useMemo(() => filteredList[Entity.hex], [filteredList]);

  const detailsPageMatch = matchPath<{
    id: string;
    section: string;
  }>(browserLocation.pathname, {
    path: "/hexes/:id/:section"
  });

  const toggleHex = useCallback(
    (h3Index: string) => {
      if (isMobile) toggleSideMenu();

      if (h3Index === detailsPageMatch?.params?.id) {
        history.push("/hexes");
      } else {
        history.push(
          `/hexes/${h3Index}/${detailsPageMatch?.params?.section || "info"}`
        );
      }
    },
    [isMobile, toggleSideMenu, detailsPageMatch?.params?.id, detailsPageMatch?.params?.section, history]
  );

  const renderListItem = useCallback(
    (index: number) => {
      const { h3Index, resolution, transmitScale, densityStatusMessage } =
        filteredHexes[index];

      return (
        <div
          key={h3Index}
          className={classNames(
            "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:ring-2 ring-inset ring-hotspotty-400 border-b border-gray-200 dark:border-gray-500 dark:ring-white",
            detailsPageMatch?.params?.id === h3Index && "ring-2"
          )}
          onClick={() => {
            toggleHex(h3Index);
          }}
        >
          <div className="flex-shrink-0">
            <span className="inline-block relative">
              {!!channels[h3Index] ? (
                <ChatAltIcon className="h-6 w-6 text-blue-500" />
              ) : (
                <HexIcon
                  className="h-6 w-6"
                  style={{
                    color: (classificationColorMap as any)[
                      getClassification(transmitScale)
                    ]
                  }}
                />
              )}
            </span>
          </div>
          <div className="flex-1 min-w-0 focus:outline-none">
            <span className="absolute inset-0" aria-hidden="true" />
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate items-center">
              Resolution {resolution}
            </p>
            <p className="text-sm font-normal text-gray-500 truncate dark:text-gray-400">
              {densityStatusMessage}
            </p>
          </div>
          <div className="text-sm text-gray-500">
            <ClassificationValue
              value={transmitScale}
              withoutTransmitScaleTooltip
            />
          </div>
        </div>
      );
    },
    [channels, detailsPageMatch?.params?.id, filteredHexes, toggleHex]
  );

  const isLoading = useMemo(
    () =>
      !getIsReady([
        LoadingKey.hotspotsAroundLocations,
        LoadingKey.discordChannels
      ]),
    [getIsReady]
  );

  const renderContent = useMemo(() => {
    if (isEmpty(filteredHexes)) {
      return (
        <>
          <div className="z-10 sticky top-0 border-b border-t border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
            <div className="text-sm font-medium">0 hexes</div>
          </div>
          <div className="px-6 pt-6 pb-4">
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              No problematic or Discord hexes to show.
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="z-10 sticky top-0 border-t border-b border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
          <div className="text-sm font-medium">
            {filteredHexes.length.toLocaleString(undefined)} hex
            {filteredHexes.length !== 1 && "es"}
          </div>
          <div className="text-xs">
            Avg simulated transmit scale:{" "}
            {mean(map(filteredHexes, "transmitScale")).toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }
            )}
          </div>
        </div>

        <VirtualScroll
          className="overflow-y-auto"
          minItemHeight={80}
          totalLength={filteredHexes.length}
          renderItem={renderListItem}
        />
      </>
    );
  }, [filteredHexes, renderListItem]);

  if (isLoading) {
    return <CenteredLoading className="h-full" />;
  }

  return (
    <>
      <div>
        <div className="flex-1 flex px-4 pb-2">
          <FilterLabels
            className="mt-2 w-full"
            entity={Entity.hex}
            filteredLabels={filteredHexLabels}
            smartLabels={keys(smartLabelFilters[Entity.hex]).map((key) =>
              getSmartLabelFromKey(key, Entity.hex)
            )}
            filterKey={LabelFilterKey.mapHexes}
          />
        </div>
      </div>

      {renderContent}
    </>
  );
};

export default HexesList;
